import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Download, Search, Inbox, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { SERVICES } from "@/data/content";

const STATUSES = ["pending", "in-progress", "closed"];

export const EnquiriesSection = () => {
  const { t } = useLanguage();
  const [enquiries, setEnquiries] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");

  const fetchEnquiries = async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch {
      setEnquiries([]);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const serviceTitle = (id) =>
    t.serviceItems[id]?.title || (id === "other" ? t.enquiry.other : id);

  const filtered = useMemo(() => {
    if (!enquiries) return [];
    return enquiries.filter((e) => {
      const q = search.toLowerCase();
      const matchesSearch = !q || e.name.toLowerCase().includes(q) || e.phone.includes(q);
      const matchesStatus = statusFilter === "all" || e.status === statusFilter;
      const matchesService = serviceFilter === "all" || e.service === serviceFilter;
      return matchesSearch && matchesStatus && matchesService;
    });
  }, [enquiries, search, statusFilter, serviceFilter]);

  const stats = useMemo(() => {
    const list = enquiries || [];
    return {
      total: list.length,
      pending: list.filter((e) => e.status === "pending").length,
      "in-progress": list.filter((e) => e.status === "in-progress").length,
      closed: list.filter((e) => e.status === "closed").length,
    };
  }, [enquiries]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      toast.success(t.admin.statusUpdated);
      fetchEnquiries();
    } catch {
      toast.error(t.enquiry.error);
    }
  };

  const deleteEnquiry = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, { withCredentials: true });
      toast.success(t.admin.deleted);
      fetchEnquiries();
    } catch {
      toast.error(t.enquiry.error);
    }
  };

  const exportCsv = () => {
    const header = "Date,Name,Phone,Email,Service,Message,Status";
    const rows = filtered.map((e) =>
      [e.created_at, e.name, e.phone, e.email || "", serviceTitle(e.service),
       `"${(e.message || "").replace(/"/g, '""')}"`, e.status].join(",")
    );
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "enquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const statCards = [
    { label: t.admin.total, value: stats.total, icon: Inbox, testid: "stat-total" },
    { label: t.admin.pending, value: stats.pending, icon: Clock, testid: "stat-pending" },
    { label: t.admin.inProgress, value: stats["in-progress"], icon: Loader2, testid: "stat-in-progress" },
    { label: t.admin.closed, value: stats.closed, icon: CheckCircle2, testid: "stat-closed" },
  ];

  return (
    <div data-testid="enquiries-section">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((s) => (
          <div key={s.testid} data-testid={s.testid} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{s.label}</p>
              <s.icon className="h-4 w-4 text-rose-600" />
            </div>
            <p className="mt-2 font-heading text-3xl font-extrabold">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="enquiry-search-input"
            className="pl-9"
            placeholder={t.admin.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter-select" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.admin.allStatuses}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={serviceFilter} onValueChange={setServiceFilter}>
          <SelectTrigger data-testid="service-filter-select" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.admin.allServices}</SelectItem>
            {SERVICES.map((s) => (
              <SelectItem key={s.id} value={s.id}>{t.serviceItems[s.id].title}</SelectItem>
            ))}
            <SelectItem value="other">{t.enquiry.other}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" data-testid="export-csv-button" onClick={exportCsv} className="gap-2">
          <Download className="h-4 w-4" /> {t.admin.exportCsv}
        </Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {!enquiries ? (
          <p className="p-8 text-center text-sm text-muted-foreground">{t.admin.loading}</p>
        ) : filtered.length === 0 ? (
          <p data-testid="no-enquiries-message" className="p-8 text-center text-sm text-muted-foreground">
            {t.admin.noEnquiries}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="enquiry-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.admin.date}</TableHead>
                  <TableHead>{t.admin.name}</TableHead>
                  <TableHead>{t.admin.phone}</TableHead>
                  <TableHead>{t.admin.service}</TableHead>
                  <TableHead>{t.admin.message}</TableHead>
                  <TableHead>{t.admin.status}</TableHead>
                  <TableHead>{t.admin.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(e.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-medium">
                      {e.name}
                      {e.email && <span className="block text-xs text-muted-foreground">{e.email}</span>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{e.phone}</TableCell>
                    <TableCell className="max-w-44 text-sm">{serviceTitle(e.service)}</TableCell>
                    <TableCell className="max-w-56 truncate text-sm text-muted-foreground">{e.message || "—"}</TableCell>
                    <TableCell>
                      <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                        <SelectTrigger data-testid={`status-select-${e.id}`} className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-enquiry-${e.id}`}
                        onClick={() => deleteEnquiry(e.id)}
                        aria-label={t.admin.actions}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};
